/**
 * The site's service worker (public/sw.js) and browser push notifications, as window.tfcPush. Every page that
 * offers notifications uses it (the Torn Planner, chain watch, the bell), so a browser has one subscription
 * and gets each notification once. The site's push settings come from window.siteConfig.push
 * (partials/_site-config), which is only there for a signed-in user when the site has its VAPID keys.
 *
 * Browsers turned on before the site had its own worker are subscribed through the Torn Planner's
 * (/planner-sw.js, scope /planner). The first page load that finds one moves the subscription to the site's
 * worker and removes the old one.
 */

const WORKER_URL = '/sw.js';
const LEGACY_SCOPE = '/planner';
const SYNCED_KEY = 'planner_push_synced';

const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
const settings = () => window.siteConfig?.push ?? null;
const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content ?? '';

async function send(method, body) {
    const response = await fetch(settings().url, {
        method,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(json.message || 'The site would not save this browser\'s notification setting.');
    }

    return json.data;
}

/** The site's public key in the form the browser wants it. */
function applicationServerKey() {
    const key = settings().public_key;
    const base64 = (key + '='.repeat((4 - (key.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');

    return Uint8Array.from(window.atob(base64), (character) => character.charCodeAt(0));
}

function rememberSync(subscription) {
    try {
        if (subscription) {
            window.localStorage.setItem(SYNCED_KEY, `${settings().user}|${subscription.endpoint}`);
        } else {
            window.localStorage.removeItem(SYNCED_KEY);
        }
    } catch {
        // Without storage the browser is simply sent to the site again on the next visit.
    }
}

function isSynced(subscription) {
    try {
        return window.localStorage.getItem(SYNCED_KEY) === `${settings().user}|${subscription.endpoint}`;
    } catch {
        return false;
    }
}

let registering = null;

/** The site's worker, registered once per page, resolved when it's active (push needs an active worker). */
function registration() {
    registering ??= navigator.serviceWorker.register(WORKER_URL, { scope: '/' }).then((registered) => {
        const worker = registered.installing || registered.waiting;
        if (registered.active || !worker) {
            return registered;
        }

        return new Promise((resolve, reject) => {
            worker.addEventListener('statechange', () => {
                if (worker.state === 'activated') {
                    resolve(registered);
                } else if (worker.state === 'redundant') {
                    registering = null;
                    reject(new Error('The site\'s service worker didn\'t install.'));
                }
            });
        });
    });

    return registering;
}

/** Tell the site about this browser's subscription, so notifications can be sent to it. */
async function save(subscription) {
    const encodings = window.PushManager.supportedContentEncodings || [];
    const state = await send('POST', {
        ...subscription.toJSON(),
        content_encoding: encodings.includes('aes128gcm') ? 'aes128gcm' : 'aesgcm',
    });
    rememberSync(subscription);

    return state;
}

async function subscribe() {
    const registered = await registration();
    const subscription = await registered.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: applicationServerKey() });

    return { subscription, state: await save(subscription) };
}

let migrating = null;

/** Move a subscription on the Torn Planner's old worker to the site's, then remove the old worker. */
function migrateLegacy() {
    migrating ??= (async () => {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const legacy = registrations.find((registered) => new URL(registered.scope).pathname === LEGACY_SCOPE);
        if (!legacy) {
            return;
        }

        const old = await legacy.pushManager.getSubscription();
        if (old) {
            if (window.Notification.permission === 'granted') {
                await subscribe();
            }
            await send('DELETE', { endpoint: old.endpoint }).catch(() => null);
            await old.unsubscribe().catch(() => false);
        }
        await legacy.unregister();
    })().catch((error) => console.error('Moving browser notifications to the site worker failed', error));

    return migrating;
}

const tfcPush = {
    supported,

    /** Whether the site can send notifications to this user at all. */
    get available() {
        return Boolean(settings());
    },

    /**
     * This browser's subscription, or null. A subscription the site hasn't seen for this user (its keys
     * changed, or someone else signed in on the browser since) is sent to it, and its reply returned as state.
     *
     * @returns {Promise<{subscription: PushSubscription|null, state: object|null}>}
     */
    async current() {
        if (!supported || !settings()) {
            return { subscription: null, state: null };
        }

        await migrateLegacy();
        const subscription = await (await registration()).pushManager.getSubscription();
        const state = subscription && !isSynced(subscription) ? await save(subscription) : null;

        return { subscription, state };
    },

    /**
     * Ask for permission and turn notifications on in this browser. Resolves with a null subscription when
     * the user didn't allow them.
     *
     * @returns {Promise<{subscription: PushSubscription|null, state: object|null}>}
     */
    async turnOn() {
        if (!supported) {
            throw new Error('This browser cannot show notifications from websites. On an iPhone or iPad, add this site to your Home Screen and open it from there.');
        }
        if (!settings()) {
            throw new Error('Browser notifications aren\'t set up on this site.');
        }

        const permission = await window.Notification.requestPermission();
        if (permission !== 'granted') {
            return { subscription: null, state: null };
        }

        await migrateLegacy();
        const existing = await (await registration()).pushManager.getSubscription();

        return existing ? { subscription: existing, state: await save(existing) } : subscribe();
    },

    /**
     * Turn notifications off in this browser.
     *
     * @returns {Promise<object>} the site's state afterwards
     */
    async turnOff(subscription) {
        const state = await send('DELETE', { endpoint: subscription.endpoint });
        rememberSync(null);
        await subscription.unsubscribe();

        return state;
    },
};

window.tfcPush = tfcPush;

// Registered on every page, so the offline page and cached assets work and the site can be installed. Not
// held back for the load event: a slow third-party script (analytics) can keep that from firing for a long time.
if ('serviceWorker' in navigator) {
    registration().catch((error) => console.error('Registering the service worker failed', error));
    if (supported && settings()) {
        migrateLegacy();
    }
}
